/**
 * Example page — Premium experience
 * Lenis smooth scroll, hero entrance, page load reveal
 */

import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { gsap, ScrollTrigger } from "./gsap";

const PREMIUM_EASING = "power3.out";
const PREMIUM_EASING_INOUT = "power3.inOut";

/***************************************
 * Lenis smooth scroll + GSAP ScrollTrigger
 ***************************************/
function initLenis() {
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    syncTouch: false,
    touchMultiplier: 1.5,
  });

  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  document.querySelectorAll('a[href^="#"]').forEach((a) => {
    const href = a.getAttribute("href");
    if (href === "#" || href === "#0") return;
    const target = document.querySelector(href);
    if (target) {
      a.addEventListener("click", (e) => {
        e.preventDefault();
        lenis.scrollTo(target, { offset: -80, duration: 1.2 });
      });
    }
  });

  window.lenis = lenis;
}

/***************************************
 * Page load mask — curtain reveal
 ***************************************/
function initPageLoadMask() {
  const mask = document.querySelector(".example-page-load-mask");
  if (!mask) return;

  gsap.to(mask, {
    clipPath: "inset(0 0 100% 0)",
    duration: 1.2,
    ease: PREMIUM_EASING_INOUT,
    delay: 0.3,
    onComplete: () => mask.remove(),
  });
}

/***************************************
 * Split title into chars for stagger (GSAP)
 ***************************************/
function splitTitleChars(el) {
  if (!el) return [];
  const text = el.textContent.trim();
  if (!text) return [];
  el.innerHTML = "";
  const chars = [];
  [...text].forEach((char) => {
    const span = document.createElement("span");
    span.className = "split-char";
    span.style.display = "inline-block";
    span.textContent = char;
    el.appendChild(span);
    chars.push(span);
  });
  return chars;
}

/***************************************
 * Hero entrance — choreographed sequence
 ***************************************/
function initHeroEntrance() {
  const hero = document.querySelector(".example-hero--main");
  if (!hero) return;

  const label = hero.querySelector(".example-hero__label");
  const title = hero.querySelector(".example-hero__title");
  const text = hero.querySelector(".example-hero__text");
  const cta = hero.querySelector(".example-hero__cta");
  const scrollIndicator = hero.querySelector(".example-hero__scroll-indicator");

  const chars = title ? splitTitleChars(title) : [];

  gsap.set([label, text, cta, scrollIndicator], { opacity: 0, y: 20 });
  if (chars.length > 0) {
    gsap.set(title, { opacity: 1 });
    gsap.set(chars, { opacity: 0, y: 30 });
  } else if (title) {
    gsap.set(title, { opacity: 0, y: 40 });
  }

  const tl = gsap.timeline({ delay: 0.6 });

  tl.from(hero.querySelector(".example-hero__parallax"), {
    scale: 1.2,
    duration: 1.6,
    ease: PREMIUM_EASING,
  });

  if (label) {
    tl.to(label, { opacity: 1, y: 0, duration: 0.7, ease: PREMIUM_EASING }, "-=1");
  }

  if (chars.length > 0) {
    tl.to(chars, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      stagger: 0.025,
      ease: PREMIUM_EASING,
    }, "-=0.7");
  } else if (title) {
    tl.to(title, { opacity: 1, y: 0, duration: 0.8, ease: PREMIUM_EASING }, "-=0.7");
  }

  if (text) {
    tl.to(text, { opacity: 1, y: 0, duration: 0.6, ease: PREMIUM_EASING }, "-=0.5");
  }
  if (cta) {
    tl.to(cta, { opacity: 1, y: 0, duration: 0.6, ease: PREMIUM_EASING }, "-=0.4");
  }
  if (scrollIndicator) {
    tl.to(scrollIndicator, { opacity: 1, y: 0, duration: 0.5, ease: PREMIUM_EASING }, "-=0.3");
  }
}

/***************************************
 * Init — only on example page
 ***************************************/
export function initExamplePage() {
  if (!document.querySelector(".example-page")) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  initLenis();
  initPageLoadMask();
  initHeroEntrance();
}
